import Link from 'next/link'
import { motion } from 'framer-motion'
import { CheckCircle, ExternalLink, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, createContext, useContext, useState } from 'react'
import type { Asset } from '@/lib/types'
import { cn } from '@/lib/utils'
import useSWRInfinite from 'swr/infinite'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import type { IUserAssetModel } from '@/lib/db/types'
import { useEditorStore } from '@/hooks/use-editor-store'
import { useProjectStore } from '@/hooks/use-project-store'

import { toast } from 'sonner'

// Loading state context for AI generation
interface CreationLoadingContextType {
  loadingCount: number
  setLoadingCount: (count: number) => void
  isGenerating: boolean
  setIsGenerating: (generating: boolean) => void
}

const CreationLoadingContext = createContext<
  CreationLoadingContextType | undefined
>(undefined)

export const CreationLoadingProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [loadingCount, setLoadingCount] = useState(0)
  const [isGenerating, setIsGenerating] = useState(false)

  return (
    <CreationLoadingContext.Provider
      value={{ loadingCount, setLoadingCount, isGenerating, setIsGenerating }}
    >
      {children}
    </CreationLoadingContext.Provider>
  )
}

export const useCreationLoading = () => {
  const context = useContext(CreationLoadingContext)
  if (!context) {
    throw new Error(
      'useCreationLoading must be used within CreationLoadingProvider',
    )
  }
  return context
}

interface CreationLibraryProps {
  query?: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  isExpanded?: boolean
  onMutateReady?: (mutate: () => void) => void
  columns?: 3 | 4 // Allow specifying column count
}

const PAGE_SIZE = 20

const getKey = (
  pageIndex: number,
  previousPageData: IUserAssetModel[] | null,
) => {
  if (previousPageData && !previousPageData.length) return null
  return `/api/me/assets?source=creation&skip=${pageIndex * PAGE_SIZE}&limit=${PAGE_SIZE}`
}

export const CreationLibrary = ({
  query,
  selectedAssets,
  onSelect,
  isExpanded = false,
  onMutateReady,
  columns = 3,
}: CreationLibraryProps) => {
  const t = useTranslations('creation-library')
  const { setIsToolboxOpen } = useProjectStore()
  const { getIsEditorOn, getIsTargetImage, getIsTargetVideo, setTargetSrc } =
    useEditorStore()

  const isEditorOn = getIsEditorOn()
  const isImageSelectedInEditor = getIsEditorOn() && getIsTargetImage()

  // Get loading state (with fallback if not in provider)
  let loadingCount = 0
  let isGenerating = false
  try {
    const loadingState = useCreationLoading()
    loadingCount = loadingState.loadingCount
    isGenerating = loadingState.isGenerating
  } catch {
    // Not in provider context, use default values
  }

  const { data, size, setSize, isLoading, mutate } = useSWRInfinite<
    IUserAssetModel[]
  >(getKey, async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error('Failed to fetch')
    return res.json()
  })

  const assets = data?.flat() || []
  const isEmpty = data?.[0]?.length === 0
  const hasNextPage = !(
    isEmpty ||
    (data && data[data.length - 1]?.length < PAGE_SIZE)
  )

  const [loadMoreRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setSize((prev) => prev + 1),
    disabled: false,
  })

  // Pass mutate function to parent for faster refresh
  useEffect(() => {
    if (onMutateReady && mutate) {
      onMutateReady(() => mutate())
    }
  }, [onMutateReady, mutate])

  const handleDelete = async (assetId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const res = await fetch(`/api/me/assets?id=${assetId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to delete')
      mutate()
      toast.success('Asset deleted successfully')
    } catch (error) {
      toast.error('Failed to delete asset')
    }
  }

  const handleImageSelect = (image: string, e: React.MouseEvent) => {
    if (isEditorOn && !isImageSelectedInEditor) return

    const assetData: Asset = {
      source: 'creation',
      type: 'image',
      link: image,
      thumbnail: image,
    }

    if (isImageSelectedInEditor) {
      setTargetSrc(image)
      setIsToolboxOpen(false)
    } else {
      onSelect(e, assetData)
    }
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_images')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  const gridCols =
    columns === 4
      ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
      : 'grid-cols-2 sm:grid-cols-3'
  const colSpan =
    columns === 4
      ? 'col-span-2 sm:col-span-3 lg:col-span-4'
      : 'col-span-2 sm:col-span-3'
  const maxSize =
    columns === 4
      ? 'max-w-[150px] max-h-[150px]'
      : 'max-w-[120px] max-h-[120px]'

  // Loading placeholder component
  const LoadingPlaceholder = ({ className }: { className?: string }) => (
    <div
      className={cn(
        'aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden relative',
        className,
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  )

  // If not expanded, render as responsive grid with limited items
  if (!isExpanded) {
    const displayAssets = assets?.slice(0, 8) || []

    return (
      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
        {/* Show loading placeholders replacing first N items, then remaining assets */}
        {isGenerating ? (
          <>
            {/* Loading placeholders replace first N items */}
            {Array.from({ length: loadingCount }).map((_, index) => (
              <LoadingPlaceholder
                key={`loading-${index}`}
                className="w-full max-w-[80px] max-h-[80px]"
              />
            ))}
            {/* Show remaining assets after loading placeholders */}
            {displayAssets.slice(loadingCount).map((asset) => (
              <motion.div
                key={asset._id.toString()}
                className={cn(
                  'aspect-square bg-gray-100 rounded-lg overflow-hidden group relative cursor-pointer w-full max-w-[80px] max-h-[80px]',
                  isEditorOn && !isImageSelectedInEditor
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                onClick={(e) => handleImageSelect(asset.asset_url, e)}
              >
                <img
                  src={asset.asset_url}
                  alt={t('image_alt')}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                {selectedAssets.some((a) => a.link === asset.asset_url) && (
                  <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Link
                    href={asset.asset_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 bg-black/50 text-gray-300 hover:text-white transition-colors rounded-full"
                    onClick={(e) => e.stopPropagation()}
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={(e) => handleDelete(asset._id.toString(), e)}
                    className="p-1 bg-white text-black rounded-full hover:bg-gray-200 transition-colors"
                    title="Delete asset"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </motion.div>
            ))}
          </>
        ) : (
          /* Normal view - show all assets */
          displayAssets.map((asset) => (
            <motion.div
              key={asset._id.toString()}
              className={cn(
                'aspect-square bg-gray-100 rounded-lg overflow-hidden group relative cursor-pointer w-full max-w-[80px] max-h-[80px]',
                isEditorOn && !isImageSelectedInEditor
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer',
              )}
              onClick={(e) => handleImageSelect(asset.asset_url, e)}
            >
              <img
                src={asset.asset_url}
                alt={t('image_alt')}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              {selectedAssets.some((a) => a.link === asset.asset_url) && (
                <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
                  <CheckCircle className="w-4 h-4" />
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link
                  href={asset.asset_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 bg-black/50 text-gray-300 hover:text-white transition-colors rounded-full"
                  onClick={(e) => e.stopPropagation()}
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3 h-3" />
                </Link>
                <button
                  onClick={(e) => handleDelete(asset._id.toString(), e)}
                  className="p-1 bg-white text-black rounded-full hover:bg-gray-200 transition-colors"
                  title="Delete asset"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    )
  }

  // Expanded view uses grid layout
  return (
    <div className={`grid ${gridCols} gap-3 w-full`}>
      {/* Show loading placeholders replacing first N items, then remaining assets */}
      {isGenerating ? (
        <>
          {/* Loading placeholders replace first N items */}
          {Array.from({ length: loadingCount }).map((_, index) => (
            <LoadingPlaceholder key={`loading-${index}`} className={maxSize} />
          ))}
          {/* Show remaining assets after loading placeholders */}
          {assets?.slice(loadingCount).map((asset) => (
            <motion.div
              key={asset._id.toString()}
              className={cn(
                'aspect-square bg-gray-100 rounded-lg overflow-hidden group relative cursor-pointer',
                maxSize,
                isEditorOn && !isImageSelectedInEditor
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer',
              )}
              onClick={(e) => handleImageSelect(asset.asset_url, e)}
            >
              <img
                src={asset.asset_url}
                alt={t('image_alt')}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              {selectedAssets.some((a) => a.link === asset.asset_url) && (
                <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
                  <CheckCircle className="w-4 h-4" />
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link
                  href={asset.asset_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 bg-black/50 text-gray-300 hover:text-white transition-colors rounded-full"
                  onClick={(e) => e.stopPropagation()}
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3 h-3" />
                </Link>
                <button
                  onClick={(e) => handleDelete(asset._id.toString(), e)}
                  className="p-1 bg-white text-black rounded-full hover:bg-gray-200 transition-colors"
                  title="Delete asset"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </>
      ) : (
        /* Normal view - show all assets */
        assets?.map((asset) => (
          <motion.div
            key={asset._id.toString()}
            className={cn(
              'aspect-square bg-gray-100 rounded-lg overflow-hidden group relative cursor-pointer',
              maxSize,
              isEditorOn && !isImageSelectedInEditor
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer',
            )}
            onClick={(e) => handleImageSelect(asset.asset_url, e)}
          >
            <img
              src={asset.asset_url}
              alt={t('image_alt')}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
            {selectedAssets.some((a) => a.link === asset.asset_url) && (
              <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
                <CheckCircle className="w-4 h-4" />
              </div>
            )}
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Link
                href={asset.asset_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 bg-black/50 text-gray-300 hover:text-white transition-colors rounded-full"
                onClick={(e) => e.stopPropagation()}
                title="Open in new tab"
              >
                <ExternalLink className="w-3 h-3" />
              </Link>
              <button
                onClick={(e) => handleDelete(asset._id.toString(), e)}
                className="p-1 bg-white text-black rounded-full hover:bg-gray-200 transition-colors"
                title="Delete asset"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        ))
      )}
      {hasNextPage && <div ref={loadMoreRef} className={`${colSpan} h-4`} />}
    </div>
  )
}
